/* eslint-disable no-unsafe-optional-chaining */
import {
  handleBackClick,
  handleItemClick,
} from "@components/C01_SiteHeader/mobileMenu/utils";
import Cta from "@components/Cta/Cta";
import Link from "@components/Link/Link";
import {
  TypeGeneralLinkFields,
  TypeGeneralLinkSkeleton,
  TypeNavLinkListFields,
  TypeNavLinkPanelFields,
  TypeNavPromoItemFields,
  TypeNavPromoItemSkeleton,
} from "@customTypes/contentful";
import { eraseUserCookies } from "@utilities/cookies";
import { MOBILE_MENU_ANCHOR } from "@utilities/e2e/e2eAnchorsList";
import { getE2EAnchorAttribute } from "@utilities/e2e/getE2EAnchor";
import { navigationGAClick } from "@utilities/navigationGAClick";
import clsx from "clsx";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { LOGIN_URL, LOGOUT_URL } from "../../../constants";
import { useNavigationStateContext } from "../../../context/navigationContext";
import styles from "./MobileLevelSection.module.scss";

type Item = TypeNavLinkPanelFields &
  TypeNavLinkListFields &
  TypeNavLinkListFields &
  TypeGeneralLinkFields &
  TypeNavPromoItemFields & {
    label?: string;
    url?: string;
  };

export const MobileLevelSection = ({
  levelData,
  isFirstLevel = false,
  secondaryLinks,
  user,
  siteRef,
}: {
  levelData: TypeNavLinkPanelFields & {
    visibility: boolean;
    items: Item[];
  };
  isFirstLevel: boolean;
  secondaryLinks?: TypeGeneralLinkSkeleton[];
  user?: unknown;
  siteRef: React.RefObject<HTMLElement | null>;
}) => {
  const { mobileMenuHistory, setMobileMenuHistory } =
    useNavigationStateContext();
  const router = useRouter();
  const [isOpenMenu, setOpenMenu] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);
  const mobileMenuHistoryRef = useRef(mobileMenuHistory);
  const isFirstLevelRef = useRef(isFirstLevel);

  useEffect(() => {
    setOpenMenu(levelData?.visibility);
  }, [levelData?.visibility]);

  useEffect(() => {
    mobileMenuHistoryRef.current = mobileMenuHistory;
  }, [mobileMenuHistory]);

  useEffect(() => {
    isFirstLevelRef.current = isFirstLevel;
  }, [isFirstLevel]);

  useEffect(() => {
    if (isOpenMenu) {
      const firstFocusableElement =
        listRef.current?.querySelectorAll("button")?.[0] ||
        listRef.current?.querySelectorAll("a")?.[0];
      firstFocusableElement?.focus();
    }
  }, [isOpenMenu]);

  useEffect(() => {
    const siteWrapper = siteRef.current;
    if (!siteWrapper) return;
    siteWrapper.classList.toggle("noscroll", isOpenMenu);

    return () => {
      siteWrapper.classList.remove("noscroll");
    };
  }, [isOpenMenu, siteRef]);

  useEffect(() => {
    if (!isOpenMenu) return;

    function onFocusIn(event: FocusEvent) {
      const target = event.target as Node;
      const list = listRef.current;
      if (!list) return;

      if (!list.contains(target)) {
        if (isFirstLevelRef.current) {
          setMobileMenuHistory([]);
          return;
        }

        handleBackClick(mobileMenuHistoryRef.current, setMobileMenuHistory);
      }
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMobileMenuHistory([]);
      }
    };

    document.addEventListener("focusin", onFocusIn);
    window.addEventListener("keydown", onKeyDown);

    // Cleanup on unmount or when menu closes
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpenMenu, setMobileMenuHistory]);

  const renderItemCaption = (menuItem) => {
    if (menuItem?.link) return menuItem?.link?.fields?.linkText; // column link
    if (menuItem?.linkText) return menuItem?.linkText; // link
    if (menuItem?.label) return menuItem?.label; // title
    return menuItem?.ownLink?.fields?.linkText; // nested item
  };

  const checkFolder = (menuItem) => {
    return menuItem?.linkList || menuItem?.column1;
  };

  const checkTitle = (menuItem) => {
    return !!menuItem?.label;
  };

  const prepareItemData = (menuItem: Item) => {
    if (menuItem?.column3)
      return [...menuItem?.column1, ...menuItem?.column2, ...menuItem?.column3];
    if (menuItem?.column2) return [...menuItem?.column1, ...menuItem?.column2];
    if (menuItem?.column1) return [...menuItem?.column1];
    return menuItem?.linkList;
  };

  const renderItems = () =>
    levelData?.items?.map((menuItem, index) => {
      let linkUrl = "";
      if (menuItem?.url) {
        linkUrl = menuItem?.url;
      } // regular link
      if (menuItem?.link?.fields?.url) {
        linkUrl = menuItem?.link?.fields?.url;
      } // column link

      return (
        <li
          key={index}
          className={clsx(
            styles.menuItem,
            checkFolder(menuItem) && styles.folder,
            checkTitle(menuItem) && styles.itemTitle,
            !isFirstLevel && styles.nestItem
          )}
        >
          <a
            href={"/"}
            {...getE2EAnchorAttribute(
              MOBILE_MENU_ANCHOR,
              `${linkUrl || renderItemCaption(menuItem)}-link`
            )}
            onClick={(e) => {
              e.preventDefault();
              if (!!menuItem?.label && !menuItem?.link) return;
              handleItemClick(
                mobileMenuHistory,
                setMobileMenuHistory,
                // @ts-expect-error old types
                prepareItemData(menuItem),
                menuItem?.ownLink?.fields,
                menuItem?.promoItems,
                menuItem?.promoCtAs
              );
              window.scrollTo(0, 0);

              if (linkUrl) {
                // link
                if (menuItem?.openInNewTab) {
                  window.location.href = linkUrl;
                } else {
                  router.push(linkUrl);
                }
              }
            }}
            key={index}
          >
            {renderItemCaption(menuItem)}
          </a>
        </li>
      );
    });

  const renderBackBtn = () => (
    <button
      onClick={() => handleBackClick(mobileMenuHistory, setMobileMenuHistory)}
      className={styles.btnBack}
    >
      Back
    </button>
  );

  const renderGoToParent = (ownLink) => {
    if (!ownLink?.url) return;
    const url = ownLink?.url || "#";
    return (
      <Link href={url}>
        <a
          href={url}
          {...getE2EAnchorAttribute(
            MOBILE_MENU_ANCHOR,
            `${ownLink?.linkText}-link`
          )}
          className={styles.goToBtn}
          onClick={(e) => {
            setMobileMenuHistory([]);
            navigationGAClick(e);
          }}
        >
          Go to {ownLink?.linkText}
        </a>
      </Link>
    );
  };

  const renderSecondaryLinks = () => {
    return (
      <ul className={styles.secondaryList}>
        {secondaryLinks?.map((item, i) => {
          if (user && item?.url === "/create-a-profile") return;
          const url = item?.url || "#";
          return (
            <li key={`${item}${i}`}>
              <Link href={url}>
                <a
                  {...getE2EAnchorAttribute(MOBILE_MENU_ANCHOR, `${url}-link`)}
                  href={url}
                  onClick={(e) => {
                    setMobileMenuHistory([]);
                    navigationGAClick(e);
                  }}
                >
                  {item?.linkText}
                </a>
              </Link>
            </li>
          );
        })}
        <li key={"login_link"}>
          <a
            {...getE2EAnchorAttribute(
              MOBILE_MENU_ANCHOR,
              user ? "logout-btn" : "login-btn"
            )}
            href={user ? LOGOUT_URL : LOGIN_URL}
            onClick={(e) => {
              if (user) {
                eraseUserCookies();
              }
              setMobileMenuHistory([]);
              navigationGAClick(e);
            }}
          >
            {user ? "Logout" : "Login"}
          </a>
        </li>
      </ul>
    );
  };

  const renderPromoCtAs = (promoCtAs: TypeGeneralLinkSkeleton[]) => (
    <div className={styles.cPromoCTAs}>
      {promoCtAs.map((promoCTA, index) => (
        <Cta
          key={`main-menu-link${index}`}
          btnTheme={styles["ctaBottom"]}
          linkText={promoCTA?.fields?.linkText}
          type={"secondary"}
          light
          url={promoCTA?.fields?.url}
          onClick={(e) => {
            navigationGAClick(e);
            router.push(promoCTA?.fields?.url);
            setMobileMenuHistory([]);
          }}
        />
      ))}
    </div>
  );

  const renderPromoItems = (promoItems: TypeNavPromoItemSkeleton[]) => (
    <div className={styles.cPromoItems}>
      {promoItems.map((promoCTA, index) => {
        const isSearch =
          promoCTA?.fields?.link?.fields?.linkText === "Course finder";
        if (isSearch) return;
        return (
          <Cta
            key={`main-menu-link${index}`}
            btnTheme={styles["ctaBottom"]}
            linkText={promoCTA?.fields?.link?.fields?.linkText}
            icon={"icon-arrow"}
            type={"tertiary"}
            light
            url={promoCTA?.fields?.link?.fields?.url}
            onClick={(e) => {
              navigationGAClick(e);
              router.push(promoCTA?.fields?.link?.fields?.url);
              setMobileMenuHistory([]);
            }}
          />
        );
      })}
    </div>
  );

  const renderBottomSection = () => (
    <div className={clsx(styles.cBottom, !secondaryLinks && styles.redLine)}>
      {levelData?.promoItems && renderPromoItems(levelData?.promoItems)}
      {levelData?.promoCtAs && renderPromoCtAs(levelData?.promoCtAs)}
      {!!secondaryLinks && renderSecondaryLinks()}
    </div>
  );

  return (
    <div className={clsx(styles.cLevel, isOpenMenu && styles.open)}>
      <ul
        inert={isOpenMenu ? undefined : true}
        ref={listRef}
        className={styles.cLevelList}
      >
        {!isFirstLevel && renderBackBtn()}
        {!isFirstLevel && renderGoToParent(levelData?.ownLink)}

        {renderItems()}
        {renderBottomSection()}
      </ul>
    </div>
  );
};

export default MobileLevelSection;
